import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { Button } from '@/components/ui/button';
import { pcmpProjectOptionsQueryOptions } from '../api/pcmpProjects.api';
import {
  PcmpProjectForm,
  type PcmpProjectFormHandle,
} from '../components/PcmpProjectForm/PcmpProjectForm';
import { useCreatePcmpProject } from '../hooks/useCreatePcmpProject';
import type { PcmpProjectFormValues } from '../types/pcmpProjectForm.schema';
import { emptyPcmpProjectFormValues, toPcmpProjectPayload } from '../utils/pcmpProjectFormMappers';

export function PcmpProjectCreatePage() {
  const router = useRouter();
  const formRef = useRef<PcmpProjectFormHandle>(null);
  const submittedValuesRef = useRef<PcmpProjectFormValues | null>(null);
  const options = useQuery(pcmpProjectOptionsQueryOptions());
  const defaultValues = useMemo(
    () => emptyPcmpProjectFormValues(format(new Date(), 'yyyy-MM-dd')),
    [],
  );

  function goToList() {
    void router.navigate({ to: '/pcmp/projects' });
  }

  const { mutate: createProject, isPending } = useCreatePcmpProject((project) => {
    // Clear the dirty state so leaving the page does not ask to discard.
    if (submittedValuesRef.current) formRef.current?.reset(submittedValuesRef.current);
    void router.navigate({
      to: '/pcmp/projects/$projectId/created',
      params: { projectId: project.id },
    });
  });

  function handleSubmit(values: PcmpProjectFormValues) {
    submittedValuesRef.current = values;
    createProject(toPcmpProjectPayload(values));
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <div className="flex items-center gap-3">
        <Button type="button" variant="outline" size="sm" onClick={goToList} className="gap-1.5">
          <ArrowLeft className="size-3.5" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold">Create PCMP Project</h1>
      </div>

      {options.isLoading ? (
        <PageLoader />
      ) : options.error || !options.data ? (
        <ErrorState
          error={options.error}
          fallbackTitle="Could not load the form options"
          onRetry={() => void options.refetch()}
          isRetrying={options.isFetching}
          fill
        />
      ) : (
        <PcmpProjectForm
          ref={formRef}
          defaultValues={defaultValues}
          options={options.data}
          onSubmit={handleSubmit}
          onCancel={goToList}
          submitLabel="Create Project"
          pendingLabel="Creating..."
          isPending={isPending}
        />
      )}
    </div>
  );
}
